import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthenticationService } from '../../services/authentication.service';
import { select, Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, take, tap } from 'rxjs';
import { SubSink } from 'subsink';
import {
  LocalLoginIdActions,
  LocalLoginIdSelectors,
} from '../../state/store-types';
import { selectQueryParams } from '@frontdoor/app/state/router.selectors';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { SupportButtonComponent } from '@shared/lib/components/support-button/support-button.component';
import { RouterModule } from '@angular/router';
import { ResetPasswordService } from '@frontdoor/app/services/reset-password.service';

@Component({
  selector: 'ba-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatListModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    SupportButtonComponent,
    RouterModule,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private _subs = new SubSink();
  readonly busy = signal<boolean>(false);
  readonly authError = signal<string>('');
  readonly passwordNotSet = signal<boolean>(false);
  readonly linkRequestSent = signal<boolean>(false);
  readonly username = this._fb.control<string>('', [
    Validators.required,
    Validators.email,
  ]);
  readonly savedLoginIds$ = this._store.pipe(
    select(LocalLoginIdSelectors.selectAll),
    map((ids) => ids.slice(-3))
  );
  constructor(
    private readonly _fb: NonNullableFormBuilder,
    private readonly _authenticator: AuthenticationService,
    private readonly _store: Store,
    private readonly _resetPasswordService: ResetPasswordService
  ) {}

  ngOnInit(): void {
    this._authenticator.logout();
    this._store.dispatch(LocalLoginIdActions.getLoginIds());
    this._subs.sink = this._store
      .pipe(
        select(selectQueryParams),
        take(1),
        filter((params) => !!params?.['username']),
        tap((params) => {
          const username = params['username'] as string;
          this.username.setValue(username);
          this.busy.set(true);
          this._checkAndDiscover(username);
        })
      )
      .subscribe();
    this._subs.sink = this.username.valueChanges.subscribe(() => {
      this.passwordNotSet.set(false);
      this.linkRequestSent.set(false);
      this.authError.set('');
    });
  }
  removeLoginId(username: string) {
    this._store.dispatch(
      LocalLoginIdActions.removeLoginId({
        username,
      })
    );
  }
  selectLoginId(username: string) {
    this.username.setValue(username);
    this.busy.set(true);
    this.passwordNotSet.set(false);
    this.linkRequestSent.set(false);
    this.authError.set('');
    username && this._checkAndDiscover(username);
  }
  private _checkAndDiscover(username: string) {
    if (!username) {
      return;
    }
    this._subs.sink = this._resetPasswordService
      .userHasSetPassword(username)
      .pipe(
        switchMap((hasSet) => {
          if (!hasSet) {
            this.passwordNotSet.set(true);
            this.busy.set(false);
            return of(null);
          }
          return this._authenticator.discover(username).pipe(
            tap(() => this.authError.set('')),
            catchError((e) => {
              this.authError.set(
                e.error?.message ||
                  'The username you entered did not match our records. Please check the entered username and try again.'
              );
              return of(null);
            }),
            tap(() => this.busy.set(false))
          );
        }),
        catchError(() => {
          this.authError.set('An error occurred. Please try again.');
          this.busy.set(false);
          return of(null);
        })
      )
      .subscribe();
  }
  requestPasswordLink() {
    const email = this.username.value;
    if (!email) return;
    this.busy.set(true);
    this._subs.sink = this._resetPasswordService
      .requestForgetPassword(email)
      .pipe(
        tap(() => {
          this.linkRequestSent.set(true);
          this.busy.set(false);
        }),
        catchError(() => {
          this.authError.set('An error occurred. Please try again.');
          this.busy.set(false);
          return of(null);
        })
      )
      .subscribe();
  }
  onSubmit() {
    this.username.markAsTouched();
    if (this.username.valid) {
      this.busy.set(true);
      this.passwordNotSet.set(false);
      this.linkRequestSent.set(false);
      this._store.dispatch(
        LocalLoginIdActions.addLoginId({
          username: this.username.value,
        })
      );
      this._checkAndDiscover(this.username.value);
    }
  }
  ngOnDestroy() {
    this._subs.unsubscribe();
  }
}
